import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Loader } from '@itds/elements/index.js';

import AdsList from '@brokerage/pages/agencies/mobile_apps/AdsList';
import CustomActionsList from '@brokerage/pages/agencies/mobile_apps/CustomActions';
import Devices from '@brokerage/pages/agencies/mobile_apps/Devices';
import General from '@brokerage/pages/agencies/mobile_apps/General';
import LimitCarriers from '@brokerage/pages/agencies/mobile_apps/LimitCarriers';
import LimitProductTypes from '@brokerage/pages/agencies/mobile_apps/LimitProductTypes';
import QuotePromotionsList from '@brokerage/pages/agencies/mobile_apps/QuotePromotionsList';
import ResourcesList from '@brokerage/pages/agencies/mobile_apps/ResourcesList';

import H1 from '@brokerage/components/H1';

import { Api } from '@brokerage/utils/Api';

// AAQ
import AAQLimitCarriers from '@brokerage/pages/agencies/mobile_apps/aaq/LimitCarriers';
import AAQLimitProductTypes from '@brokerage/pages/agencies/mobile_apps/aaq/LimitProductTypes';
import AAQCustomActions from '@brokerage/pages/agencies/mobile_apps/aaq/CustomActions';
import AAQQuotePromotionsList from '@brokerage/pages/agencies/mobile_apps/aaq/QuotePromotionsList';

const TAB_CONFIG = {
  overview: {
    label: 'General',
    verticalTabs: ['overview', 'ads', 'devices', 'resources'].map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1)
    }))
  },
  annuity: {
    label: 'Annuity',
    verticalTabs: ['limit_products', 'limit_carriers', 'custom_actions', 'quote_promotions'].map(
      key => ({
        key,
        label: key.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())
      })
    )
  },
  life: {
    label: 'Life',
    verticalTabs: ['limit_products', 'limit_carriers', 'custom_actions', 'quote_promotions'].map(
      key => ({
        key,
        label: key.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())
      })
    )
  }
};

const TOP_TAB = Object.keys(TAB_CONFIG).map(key => ({
  key,
  label: TAB_CONFIG[key].label
}));

export const Show = () => {
  const params = useParams();

  const [mobile_app, setAgencyMobileApp] = useState();
  const [loading, setLoading] = useState(true);
  const [error_message, setErrorMessage] = useState();
  const [success_message, setSuccessMessage] = useState();

  const [selectedProductTab, setSelectedProductTab] = useState('overview');
  const [selectedVerticalTab, setSelectedVerticalTab] = useState('overview');

  const getVerticalTabs = () => TAB_CONFIG[selectedProductTab]?.verticalTabs || [];

  useEffect(() => {
    Api.loadAgencyMobileApp(params.id).then(({ data }) => {
      setAgencyMobileApp(data);

      setLoading(false);
    });
  }, []);

  const updateAgencyMobileApp = input_fields => {
    window.scrollTo(0, 0);

    Api.updateAgencyMobileApp(params.id, input_fields)
      .then(({ data }) => {
        setAgencyMobileApp(data);
        setSuccessMessage('Agency Mobile App updated successfully');

        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setErrorMessage('Unable to update Agency Mobile App');

        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const child_props = {
    mobile_app,
    setErrorMessage,
    setSuccessMessage,
    updateAgencyMobileApp
  };

  function renderVerticalTabContent() {
    const tabContentMap = {
      overview: {
        overview: <General {...child_props} />,
        ads: <AdsList {...child_props} />,
        devices: <Devices {...child_props} />,
        resources: <ResourcesList {...child_props} />
      },
      annuity: {
        limit_products: <AAQLimitProductTypes {...child_props} />,
        limit_carriers: <AAQLimitCarriers {...child_props} />,
        custom_actions: <AAQCustomActions {...child_props} />,
        quote_promotions: <AAQQuotePromotionsList {...child_props} />
      },
      life: {
        limit_products: <LimitProductTypes {...child_props} />,
        limit_carriers: <LimitCarriers {...child_props} />,
        custom_actions: <CustomActionsList {...child_props} />,
        quote_promotions: <QuotePromotionsList {...child_props} />
      }
    };

    return tabContentMap[selectedProductTab]?.[selectedVerticalTab] || null;
  }

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message && <Alert type='error'>{error_message}</Alert>}
      {success_message && <Alert type='success'>{success_message}</Alert>}

      <H1>{mobile_app.name}</H1>

      <div style={TopTab}>
        {TOP_TAB.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => {
              setSelectedProductTab(key);
              setSelectedVerticalTab(key === 'overview' ? 'overview' : 'limit_products');
            }}
            style={{
              ...TopTabButton,
              fontWeight: selectedProductTab === key ? 700 : 400,
              color: selectedProductTab === key ? '#1976d2' : '#333',
              borderBottom: selectedProductTab === key ? '2px solid #1976d2' : 'none'
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', minHeight: 400 }}>
        <div style={SideBar}>
          {getVerticalTabs().map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSelectedVerticalTab(key)}
              style={{
                ...SideBarButton,
                fontWeight: selectedVerticalTab === key ? 600 : 400,
                color: selectedVerticalTab === key ? '#1976d2' : '#333',
                borderLeft: selectedVerticalTab === key ? '3px solid #1976d2' : 'none',
                backgroundColor: selectedVerticalTab === key ? '#f5faff' : 'transparent'
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, padding: 35 }}>{renderVerticalTabContent()}</div>
      </div>
    </>
  );
};

const TopTab = {
  display: 'flex',
  gap: 16,
  marginBottom: 24,
  justifyContent: 'center',
  alignItems: 'center'
};
const SideBar = {
  width: 220,
  borderRight: '1px solid #e0e0e0',
  padding: '24px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 8
};
const TopTabButton = {
  background: 'none',
  border: 'none',
  fontSize: 18,
  padding: '8px 24px',
  cursor: 'pointer'
};
const SideBarButton = {
  background: 'none',
  border: 'none',
  fontSize: 16,
  padding: '10px 18px',
  textAlign: 'left'
};
